import React, { useState, useEffect, useRef, ReactNode, useMemo, useCallback } from 'react';
import { ChevronRight } from 'lucide-react';

interface MobileCarouselProps {
  children: ReactNode[];
  itemWidth?: string; // CSS width value (e.g., '85vw', '300px')
  gap?: number; // Gap between items in pixels
  autoScroll?: boolean;
  autoScrollDelay?: number; // in milliseconds
  showControls?: boolean;
  showPagination?: boolean;
  className?: string;
  carouselId?: string;
}

const CarouselButton: React.FC<{
  direction: 'left' | 'right';
  onClick: () => void;
  isActive: boolean;
}> = React.memo(({ direction, onClick, isActive }) => (
  <button 
    onClick={onClick}
    className={`absolute top-1/2 -translate-y-1/2 z-10 bg-white rounded-full shadow-lg p-2 ${
      direction === 'left' ? 'left-2' : 'right-2'
    }`}
    role="button"
    aria-label={`Scroll ${direction}`}
    style={{ transform: `translateY(-50%)` }} // Ensure transform works on all browsers
  >
    <div 
      className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100"
      aria-hidden="true"
    >
      <ChevronRight 
        className={`w-5 h-5 text-gray-600 ${direction === 'left' ? 'transform rotate-180' : ''}`}
        aria-hidden="true"
      />
    </div>
  </button>
));

CarouselButton.displayName = 'CarouselButton';

const PaginationDot: React.FC<{
  index: number;
  isActive: boolean;
  onClick: () => void;
}> = React.memo(({ index, isActive, onClick }) => (
  <button 
    onClick={onClick}
    className="relative min-w-[44px] min-h-[44px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
    role="button"
    aria-label={`Go to slide ${index + 1}`}
    aria-current={isActive ? "true" : "false"}
  >
    <span
      className={`w-3 h-3 rounded-full transition-colors duration-300 ${
        isActive ? 'bg-blue-600' : 'bg-gray-300'
      }`}
      aria-hidden="true"
    />
  </button>
));

PaginationDot.displayName = 'PaginationDot';

export default function MobileCarousel({
  children,
  itemWidth = '85vw',
  gap = 16,
  autoScroll = true,
  autoScrollDelay = 5000,
  showControls = true,
  showPagination = true,
  className = '',
  carouselId = 'default'
}: MobileCarouselProps) {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const childrenCount = React.Children.count(children);

  // Calculate item width based on viewport dimensions
  const itemWidthValue = useMemo(() => {
    if (typeof window === 'undefined') return 0;
    
    if (itemWidth.includes('vw')) {
      return viewportWidth * (parseInt(itemWidth) / 100);
    } else if (itemWidth.includes('px')) {
      return parseInt(itemWidth);
    } else {
      // Default to percentage of viewport if unit is not recognized
      return viewportWidth * 0.85;
    }
  }, [itemWidth, viewportWidth]);

  const slideWidth = useMemo(() => itemWidthValue + gap, [itemWidthValue, gap]);

  // Handle scroll position changes
  const updateScrollButtons = useCallback(() => {
    if (!carouselRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    
    // Add a small buffer for cross-browser compatibility
    const scrollBuffer = 2;
    setCanScrollLeft(scrollLeft > scrollBuffer);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - scrollBuffer);
    
    // Calculate current slide index based on scroll position
    const slideIndex = Math.round(scrollLeft / slideWidth);
    setActiveSlide(Math.min(Math.max(0, slideIndex), childrenCount - 1));
  }, [slideWidth, childrenCount]);

  // Scroll to a specific slide
  const scrollToSlide = useCallback((index: number) => {
    if (!carouselRef.current) return;
    
    const targetIndex = Math.min(Math.max(0, index), childrenCount - 1);
    const targetScroll = targetIndex * slideWidth;
    
    carouselRef.current.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }, [slideWidth, childrenCount]);

  const scrollCarousel = useCallback((direction: 'left' | 'right') => {
    if (!carouselRef.current) return;
    
    const nextSlide = direction === 'left' 
      ? Math.max(0, activeSlide - 1)
      : Math.min(childrenCount - 1, activeSlide + 1);
    
    scrollToSlide(nextSlide);
  }, [activeSlide, childrenCount, scrollToSlide]);

  // Auto-scroll functionality
  const startAutoScroll = useCallback(() => {
    if (isAutoScrolling || !autoScroll || childrenCount <= 1) return;
    setIsAutoScrolling(true);

    autoScrollIntervalRef.current = setInterval(() => {
      if (carouselRef.current) {
        const nextSlide = (activeSlide + 1) % childrenCount;
        scrollToSlide(nextSlide);
      }
    }, autoScrollDelay);
  }, [autoScroll, autoScrollDelay, isAutoScrolling, activeSlide, childrenCount, scrollToSlide]);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
    setIsAutoScrolling(false);
  }, []);

  // Handle viewport resizing
  const handleResize = useCallback(() => {
    if (typeof window !== 'undefined') {
      setViewportWidth(window.innerWidth);
    }
    updateScrollButtons();
  }, [updateScrollButtons]);

  // Set up scroll event listeners and intersection observer
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleScroll = () => {
      updateScrollButtons();
    };

    // Use better approach for detecting visibility
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && autoScroll && childrenCount > 1) {
            // Only start auto-scroll if the carousel is fully visible
            // and has been properly positioned in the viewport
            if (entry.intersectionRatio >= 0.95 && entry.boundingClientRect.top >= 0) {
              startAutoScroll();
            } else {
              stopAutoScroll();
            }
          } else {
            stopAutoScroll();
          }
        });
      },
      { 
        threshold: [0, 0.95], // Observe both when element enters/exits and when it's 95% visible
        rootMargin: '0px' // No margin to ensure accurate visibility detection
      }
    );

    // Add a small delay before observing to ensure proper positioning
    const timeoutId = setTimeout(() => {
      observer.observe(carousel);
    }, 100);

    carousel.addEventListener('scroll', handleScroll, { passive: true });
    updateScrollButtons();

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
      carousel.removeEventListener('scroll', handleScroll);
      stopAutoScroll();
    };
  }, [autoScroll, childrenCount, startAutoScroll, stopAutoScroll, updateScrollButtons]);

  // Handle touch events for swiping
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    let touchStartX = 0;
    let touchStartY = 0;
    let isSwiping = false;
    
    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isSwiping = true;
      stopAutoScroll();
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      if (!isSwiping) return;
      
      // Calculate both horizontal and vertical movement
      const touchMoveX = e.touches[0].clientX;
      const touchMoveY = e.touches[0].clientY;
      const diffX = touchStartX - touchMoveX;
      const diffY = touchStartY - touchMoveY;
      
      // If vertical scrolling is more significant, allow page to scroll
      if (Math.abs(diffY) > Math.abs(diffX)) {
        isSwiping = false;
        return;
      }
      
      // Prevent page scrolling when swiping horizontally
      if (Math.abs(diffX) > 10) {
        e.preventDefault();
      }
    };
    
    const handleTouchEnd = (e: TouchEvent) => {
      if (!isSwiping) return;
      
      const touchEndX = e.changedTouches[0].clientX;
      const diffX = touchStartX - touchEndX;
      
      // Only consider it a swipe if movement is significant
      if (Math.abs(diffX) > 50) {
        scrollCarousel(diffX > 0 ? 'right' : 'left');
      }
      
      isSwiping = false;
    };
    
    carousel.addEventListener('touchstart', handleTouchStart, { passive: true });
    carousel.addEventListener('touchmove', handleTouchMove, { passive: false });
    carousel.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      carousel.removeEventListener('touchstart', handleTouchStart);
      carousel.removeEventListener('touchmove', handleTouchMove);
      carousel.removeEventListener('touchend', handleTouchEnd);
    };
  }, [scrollCarousel, stopAutoScroll]);

  // Handle window resize
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize, { passive: true });
      
      // Initial setup
      handleResize();
      
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [handleResize]);

  // Prevent errors when rendering on server
  if (typeof window === 'undefined' || childrenCount === 0) {
    return null;
  }

  return (
    <div 
      className={`relative ${className}`}
      role="region"
      aria-roledescription="carousel"
      aria-label="Feature showcase carousel"
      id={`carousel-${carouselId}`}
    >
      {/* Controls */}
      {showControls && childrenCount > 1 && (
        <div className="relative" role="group" aria-label="Carousel navigation">
          {canScrollLeft && (
            <CarouselButton
              direction="left"
              onClick={() => {
                scrollCarousel('left');
                stopAutoScroll();
              }}
              isActive={canScrollLeft}
            />
          )}
          
          {canScrollRight && (
            <CarouselButton
              direction="right"
              onClick={() => {
                scrollCarousel('right');
                stopAutoScroll();
              }}
              isActive={canScrollRight}
            />
          )}
        </div>
      )}
      
      {/* Carousel container */}
      <div 
        ref={carouselRef}
        className="overflow-x-auto scrollbar-hide scroll-smooth pb-8 -mx-4 snap-x snap-mandatory"
        style={{ WebkitOverflowScrolling: 'touch' }} // Improve momentum scrolling on iOS
        role="region"
        aria-label="Slides container"
      >
        <div 
          className="flex px-4" 
          style={{ gap: `${gap}px` }}
        >
          {React.Children.map(children, (child, index) => (
            <div 
              key={index} 
              className="flex-shrink-0 snap-center"
              style={{ width: itemWidth }}
              role="tabpanel"
              id={`${carouselId}-slide-${index + 1}`}
              aria-labelledby={`${carouselId}-tab-${index + 1}`}
              tabIndex={activeSlide === index ? 0 : -1}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
      
      {/* Pagination dots */}
      {showPagination && childrenCount > 1 && (
        <div 
          className="flex justify-center mt-4"
          role="tablist"
          id={`${carouselId}-tabs`}
          aria-label="Carousel navigation"
          aria-orientation="horizontal"
        >
          <div className="flex space-x-4">
            {Array.from({ length: childrenCount }).map((_, index) => (
              <button
                key={index}
                role="tab"
                aria-selected={activeSlide === index}
                aria-controls={`${carouselId}-slide-${index + 1}`}
                id={`${carouselId}-tab-${index + 1}`}
                className="relative min-w-[44px] min-h-[44px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                onClick={() => {
                  scrollToSlide(index);
                  stopAutoScroll();
                }}
                tabIndex={activeSlide === index ? 0 : -1}
              >
                <span
                  className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                    activeSlide === index ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  aria-hidden="true"
                />
                <span className="sr-only">Go to slide {index + 1}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Screen reader announcements */}
      <div className="sr-only" aria-live="polite">
        Slide {activeSlide + 1} of {childrenCount}
      </div>
    </div>
  );
} 